import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import notify from "devextreme/ui/notify";
import { useAuth } from "../../contexts/auth";
import { baseAxios } from "../../utils/config";
import { useLocationBusinessHours } from "../../hooks/useLocationBusinessHours";

const GET_LOCATIONS_API = "gateway/3lp5nnrkw8";
const UPDATE_LOCATION_API = "gateway/v0gi2lnyki";

const US_TIME_ZONES = [
  "Eastern Standard Time (EST)",
  "Central Standard Time (CST)",
  "Mountain Standard Time (MST)",
  "Pacific Standard Time (PST)",
  "Alaska Standard Time (AKST)",
  "Hawaii-Aleutian Standard Time (HAST)",
];

const DAYS = [
  { key: "monday", label: "Monday" },
  { key: "tuesday", label: "Tuesday" },
  { key: "wednesday", label: "Wednesday" },
  { key: "thursday", label: "Thursday" },
  { key: "friday", label: "Friday" },
  { key: "saturday", label: "Saturday" },
  { key: "sunday", label: "Sunday" },
];

const TIME_OPTIONS = Array.from({ length: 48 }, (_, index) => {
  const hour = Math.floor(index / 2);
  const minute = index % 2 === 0 ? "00" : "30";
  return `${hour.toString().padStart(2, "0")}:${minute}`;
});

const sectionLabelSx = {
  display: "block",
  fontSize: "0.7rem",
  fontWeight: 700,
  letterSpacing: "0.08em",
  color: "text.secondary",
  mb: 1.25,
};

const cardSx = {
  bgcolor: "background.paper",
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 1,
  p: 2.5,
};

const closedHoursSelectSx = {
  "& .MuiOutlinedInput-root.Mui-disabled": {
    bgcolor: (theme) =>
      theme.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "grey.200",
    opacity: 1,
  },
  "& .MuiSelect-select.Mui-disabled": {
    color: "text.disabled",
    WebkitTextFillColor: (theme) => theme.palette.text.disabled,
  },
  "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline": {
    borderColor: (theme) =>
      theme.palette.mode === "dark" ? "rgba(255,255,255,0.2)" : "grey.300",
  },
  "& .MuiSvgIcon-root.Mui-disabled": {
    color: "action.disabled",
  },
};

function createInitialDaySettings() {
  return Object.fromEntries(
    DAYS.map(({ key }) => [key, { isOpen: false, open: "", close: "" }])
  );
}

/**
 * Map a saved location_business_hours row onto the form state. Days stored as
 * "closed" (or with a time the picker does not offer) start unticked.
 */
export function daySettingsFromSavedHours(savedHours) {
  const settings = createInitialDaySettings();
  if (!savedHours) return settings;
  DAYS.forEach(({ key }) => {
    const open = savedHours[`${key}_open`];
    const close = savedHours[`${key}_close`];
    if (TIME_OPTIONS.includes(open) && TIME_OPTIONS.includes(close)) {
      settings[key] = { isOpen: true, open, close };
    }
  });
  return settings;
}

function isTimeAtLeast30MinsLater(openTime, closeTime) {
  if (!openTime || !closeTime) return false;
  const [openHour, openMinute] = openTime.split(":").map(Number);
  const [closeHour, closeMinute] = closeTime.split(":").map(Number);
  const openTimeInMinutes = openHour * 60 + openMinute;
  const closeTimeInMinutes = closeHour * 60 + closeMinute;
  return closeTimeInMinutes - openTimeInMinutes >= 30;
}

function isValidPhoneNumber(value) {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return false;
  return !Number.isNaN(Number(trimmed)) && parseInt(trimmed, 10) === Number(trimmed);
}

export default function ConfigureSalonForm() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [loadingLocations, setLoadingLocations] = useState(true);
  const [saving, setSaving] = useState(false);
  const [locations, setLocations] = useState([]);

  const [locationId, setLocationId] = useState("");
  const [salonAddress, setSalonAddress] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [timeZone, setTimeZone] = useState("");
  const [daySettings, setDaySettings] = useState(createInitialDaySettings);
  const [fieldErrors, setFieldErrors] = useState({});
  // Salon whose saved hours have been copied into the form state.
  const [seededLocationId, setSeededLocationId] = useState(null);

  const userCredentials = useMemo(
    () => ({
      Barcode: user.barcode,
      Role: user.userRoles,
      Organization: user.org,
      Location: user.location_id,
    }),
    [user.barcode, user.userRoles, user.org, user.location_id]
  );

  const isSystemAdmin = userCredentials.Role.includes(3);

  useEffect(() => {
    async function loadLocations() {
      setLoadingLocations(true);
      try {
        if (isSystemAdmin) {
          const res = await baseAxios.post(GET_LOCATIONS_API, userCredentials);
          setLocations(Array.isArray(res.data) ? res.data : []);
        } else {
          setLocations([
            {
              location_id: user.location_id,
              location_name: user.location_name,
              organization_id: parseInt(user.org, 10),
              location_address: user.location_address,
              location_phone: user.location_phone,
            },
          ]);
        }
      } catch {
        setLocations([]);
        notify("Could not load salon locations.", "error", 3000);
      } finally {
        setLoadingLocations(false);
      }
    }

    loadLocations();
  }, [isSystemAdmin, user.location_id, user.location_name, user.org, userCredentials]);

  useEffect(() => {
    if (locations.length === 1 && !locationId) {
      setLocationId(locations[0].location_id);
    }
  }, [locations, locationId]);

  useEffect(() => {
    if (!locationId) return;
    const selected = locations.find(
      (location) => String(location.location_id) === String(locationId)
    );
    if (!selected) return;
    setSalonAddress(selected.location_address ?? "");
    setPhoneNumber(
      selected.location_phone != null ? String(selected.location_phone) : ""
    );
  }, [locationId, locations]);

  const selectedLocation = locations.find(
    (location) => String(location.location_id) === String(locationId)
  );
  const {
    businessHours,
    loading: loadingHours,
    error: hoursError,
  } = useLocationBusinessHours(
    selectedLocation?.organization_id ?? user.org,
    locationId
  );
  const hoursLoadFailed = Boolean(hoursError);

  // Copy the saved hours into the editable form once they arrive for the
  // selected salon. Adjusting state during render (not in an effect) avoids
  // an extra render with stale values. Saving always overwrites every day, so
  // Save stays disabled until this has happened.
  if (
    locationId &&
    !loadingHours &&
    !hoursError &&
    seededLocationId !== String(locationId)
  ) {
    setSeededLocationId(String(locationId));
    setTimeZone(
      US_TIME_ZONES.includes(businessHours?.time_zone) ? businessHours.time_zone : ""
    );
    setDaySettings(daySettingsFromSavedHours(businessHours));
  }

  const hoursReady =
    Boolean(locationId) && seededLocationId === String(locationId);
  const busy =
    loadingLocations ||
    saving ||
    (Boolean(locationId) && !hoursReady && !hoursLoadFailed);

  const updateDaySetting = useCallback((dayKey, patch) => {
    setDaySettings((prev) => ({
      ...prev,
      [dayKey]: { ...prev[dayKey], ...patch },
    }));
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next[`${dayKey}Open`];
      delete next[`${dayKey}Close`];
      return next;
    });
  }, []);

  const clearFieldError = useCallback((field) => {
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const validateForm = useCallback(() => {
    const errors = {};

    if (!locationId) errors.location = "Please choose a location";
    if (!String(salonAddress).trim()) errors.salonAddress = "Please enter an address";
    if (!isValidPhoneNumber(phoneNumber)) {
      errors.phoneNumber = "Please enter a valid phone number";
    }
    if (!timeZone) errors.timeZone = "Please choose a time zone";

    DAYS.forEach(({ key, label }) => {
      const day = daySettings[key];
      if (!day.isOpen) return;

      if (!day.open) {
        errors[`${key}Open`] = `${label} opening time is required`;
      }
      if (!day.close) {
        errors[`${key}Close`] = `${label} closing time is required`;
      } else if (day.open && !isTimeAtLeast30MinsLater(day.open, day.close)) {
        errors[`${key}Close`] =
          "Closing time must be at least 30 minutes after opening time";
      }
    });

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }, [daySettings, locationId, phoneNumber, salonAddress, timeZone]);

  const buildPayload = useCallback(() => {
    const payload = {
      location: locationId,
      salonAddress: String(salonAddress).trim(),
      phoneNumber: String(phoneNumber).trim(),
      timeZone,
    };

    DAYS.forEach(({ key }) => {
      const dayKey = key;
      const capitalized = dayKey.charAt(0).toUpperCase() + dayKey.slice(1);
      const day = daySettings[dayKey];

      payload[`is${capitalized}Open`] = day.isOpen;
      payload[`${dayKey}Open`] = day.isOpen ? day.open : "closed";
      payload[`${dayKey}Close`] = day.isOpen ? day.close : "closed";
    });

    return payload;
  }, [daySettings, locationId, phoneNumber, salonAddress, timeZone]);

  const onSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      if (hoursLoadFailed) {
        notify(
          "Saved business hours were not loaded. Reload the page before saving.",
          "error",
          5000
        );
        return;
      }
      if (!validateForm()) return;

      setSaving(true);
      try {
        const res = await baseAxios.post(UPDATE_LOCATION_API, buildPayload());
        if (res.status === 200) {
          notify("Success", "success", 3000);
          setTimeout(() => {
            navigate(0);
          }, 3000);
        } else {
          notify("Operation failed", "error", 5000);
        }
      } catch (error) {
        notify(
          "Operation failed: " + (error?.message ?? "Unknown error"),
          "error",
          5000
        );
      } finally {
        setSaving(false);
      }
    },
    [buildPayload, hoursLoadFailed, navigate, validateForm]
  );

  return (
    <Box sx={{ px: 8, py: 3 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          mt: 1.5,
          mb: 2,
        }}
      >
        <Box>
          <Typography fontWeight={600} sx={{ fontSize: "1.5rem" }}>
            Salon Settings
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure salon contact details, time zone, and weekly business hours.
          </Typography>
          {hoursLoadFailed ? (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              Could not load the saved business hours. Reload the page before
              saving.
            </Typography>
          ) : null}
        </Box>
        {!loadingLocations ? (
          <Button
            type="submit"
            form="salon-settings-form"
            variant="contained"
            color="primary"
            disabled={busy || hoursLoadFailed}
            sx={{
              minWidth: 180,
              minHeight: 40,
              px: 3,
              textTransform: "none",
              fontWeight: 700,
              boxShadow: "none",
              "&:hover": { boxShadow: "none" },
            }}
          >
            {saving ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Save salon settings"
            )}
          </Button>
        ) : null}
      </Box>

      {loadingLocations ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
          <CircularProgress size={32} color="primary" />
        </Box>
      ) : (
        <Box
          component="form"
          id="salon-settings-form"
          onSubmit={onSubmit}
        >
          <Grid container spacing={2.5} alignItems="stretch">
            <Grid item xs={12} lg={4}>
              <Box sx={{ ...cardSx, height: "100%" }}>
                <Typography variant="overline" sx={sectionLabelSx}>
                  Salon details
                </Typography>
                <Stack spacing={2}>
                  <FormControl
                    fullWidth
                    size="small"
                    required
                    error={Boolean(fieldErrors.location)}
                    disabled={busy || (!isSystemAdmin && locations.length <= 1)}
                  >
                    <InputLabel id="salon-location-label">Salon</InputLabel>
                    <Select
                      labelId="salon-location-label"
                      label="Salon"
                      value={locationId}
                      onChange={(event) => {
                        setLocationId(event.target.value);
                        setTimeZone("");
                        setDaySettings(createInitialDaySettings());
                        clearFieldError("location");
                      }}
                    >
                      {locations.map((location) => (
                        <MenuItem
                          key={location.location_id}
                          value={location.location_id}
                        >
                          {location.location_name}
                        </MenuItem>
                      ))}
                    </Select>
                    {fieldErrors.location ? (
                      <FormHelperText>{fieldErrors.location}</FormHelperText>
                    ) : null}
                  </FormControl>

                  <TextField
                    label="Salon address"
                    value={salonAddress}
                    onChange={(event) => {
                      setSalonAddress(event.target.value);
                      clearFieldError("salonAddress");
                    }}
                    required
                    fullWidth
                    size="small"
                    disabled={busy}
                    error={Boolean(fieldErrors.salonAddress)}
                    helperText={fieldErrors.salonAddress}
                    multiline
                    minRows={2}
                  />

                  <TextField
                    label="Phone number"
                    value={phoneNumber}
                    onChange={(event) => {
                      setPhoneNumber(event.target.value);
                      clearFieldError("phoneNumber");
                    }}
                    required
                    fullWidth
                    size="small"
                    disabled={busy}
                    error={Boolean(fieldErrors.phoneNumber)}
                    helperText={fieldErrors.phoneNumber}
                  />

                  <FormControl
                    fullWidth
                    size="small"
                    required
                    error={Boolean(fieldErrors.timeZone)}
                    disabled={busy}
                  >
                    <InputLabel id="salon-timezone-label">Time zone</InputLabel>
                    <Select
                      labelId="salon-timezone-label"
                      label="Time zone"
                      value={timeZone}
                      onChange={(event) => {
                        setTimeZone(event.target.value);
                        clearFieldError("timeZone");
                      }}
                    >
                      {US_TIME_ZONES.map((zone) => (
                        <MenuItem key={zone} value={zone}>
                          {zone}
                        </MenuItem>
                      ))}
                    </Select>
                    {fieldErrors.timeZone ? (
                      <FormHelperText>{fieldErrors.timeZone}</FormHelperText>
                    ) : null}
                  </FormControl>
                </Stack>
              </Box>
            </Grid>

            <Grid item xs={12} lg={8}>
              <Box sx={{ ...cardSx, height: "100%" }}>
                <Typography variant="overline" sx={sectionLabelSx}>
                  Business hours
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Mark open days and set hours. Closed days are saved as closed.
                </Typography>

                <TableContainer
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    overflow: "hidden",
                  }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: "secondary.main" }}>
                        <TableCell
                          sx={{
                            fontWeight: 700,
                            width: 140,
                            color: "common.white",
                            bgcolor: "secondary.main",
                          }}
                        >
                          Day
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 700,
                            width: 160,
                            color: "common.white",
                            bgcolor: "secondary.main",
                          }}
                        >
                          Open
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 700,
                            width: 160,
                            color: "common.white",
                            bgcolor: "secondary.main",
                          }}
                        >
                          Close
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {DAYS.map(({ key, label }) => {
                        const day = daySettings[key];
                        return (
                          <TableRow
                            key={key}
                            hover
                            sx={{
                              "&:last-child td": { borderBottom: 0 },
                            }}
                          >
                            <TableCell sx={{ py: 1.25 }}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    size="small"
                                    color="primary"
                                    checked={day.isOpen}
                                    onChange={(event) =>
                                      updateDaySetting(key, {
                                        isOpen: event.target.checked,
                                        ...(event.target.checked
                                          ? {}
                                          : { open: "", close: "" }),
                                      })
                                    }
                                    disabled={busy}
                                  />
                                }
                                label={
                                  <Typography variant="body2" fontWeight={600}>
                                    {label}
                                  </Typography>
                                }
                                sx={{ m: 0 }}
                              />
                            </TableCell>
                            <TableCell sx={{ py: 1.25 }}>
                              <FormControl
                                fullWidth
                                size="small"
                                disabled={busy || !day.isOpen}
                                error={Boolean(fieldErrors[`${key}Open`])}
                                sx={!day.isOpen ? closedHoursSelectSx : undefined}
                              >
                                <Select
                                  displayEmpty
                                  value={day.open}
                                  onChange={(event) =>
                                    updateDaySetting(key, {
                                      open: event.target.value,
                                    })
                                  }
                                  renderValue={(selected) =>
                                    selected || (
                                      <Typography
                                        component="span"
                                        variant="body2"
                                        color={
                                          day.isOpen
                                            ? "text.secondary"
                                            : "text.disabled"
                                        }
                                      >
                                        Open
                                      </Typography>
                                    )
                                  }
                                >
                                  {TIME_OPTIONS.map((time) => (
                                    <MenuItem
                                      key={`${key}-open-${time}`}
                                      value={time}
                                    >
                                      {time}
                                    </MenuItem>
                                  ))}
                                </Select>
                                {fieldErrors[`${key}Open`] ? (
                                  <FormHelperText>
                                    {fieldErrors[`${key}Open`]}
                                  </FormHelperText>
                                ) : null}
                              </FormControl>
                            </TableCell>
                            <TableCell sx={{ py: 1.25 }}>
                              <FormControl
                                fullWidth
                                size="small"
                                disabled={busy || !day.isOpen}
                                error={Boolean(fieldErrors[`${key}Close`])}
                                sx={!day.isOpen ? closedHoursSelectSx : undefined}
                              >
                                <Select
                                  displayEmpty
                                  value={day.close}
                                  onChange={(event) =>
                                    updateDaySetting(key, {
                                      close: event.target.value,
                                    })
                                  }
                                  renderValue={(selected) =>
                                    selected || (
                                      <Typography
                                        component="span"
                                        variant="body2"
                                        color={
                                          day.isOpen
                                            ? "text.secondary"
                                            : "text.disabled"
                                        }
                                      >
                                        Close
                                      </Typography>
                                    )
                                  }
                                >
                                  {TIME_OPTIONS.map((time) => (
                                    <MenuItem
                                      key={`${key}-close-${time}`}
                                      value={time}
                                    >
                                      {time}
                                    </MenuItem>
                                  ))}
                                </Select>
                                {fieldErrors[`${key}Close`] ? (
                                  <FormHelperText>
                                    {fieldErrors[`${key}Close`]}
                                  </FormHelperText>
                                ) : null}
                              </FormControl>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Grid>
          </Grid>
        </Box>
      )}
    </Box>
  );
}
